import { Link } from "react-router-dom";
import { Disc3, CircleGauge, Wrench, Search, AlertTriangle, CheckCircle2 } from "lucide-react";
import { SEO } from "../components/SEO";
import { SectionHeading, Reveal, Eyebrow } from "../components/Reveal";
import { CTAPair, QuoteButton } from "../components/CTAButtons";
import { FAQSection } from "../components/FAQSection";
import { CTABand } from "../components/CTABand";
import { LOCATIONS } from "../data/locations";

const SERVICES = [
  {
    icon: Disc3,
    title: "Front Brake Replacement",
    text: "Front brakes do most of your stopping — often 70% or more. We replace front pads and rotors on site, bed them in correctly, and road-test the result.",
  },
  {
    icon: Disc3,
    title: "Rear Brake Replacement",
    text: "Rear pads, rotors and parking-brake hardware serviced at your home or office. Critical for stability and even wear across all four corners.",
  },
  {
    icon: Wrench,
    title: "Brake Pads",
    text: "Quality ceramic or semi-metallic pads matched to your vehicle and driving. We explain the difference and only install what your car actually needs.",
  },
  {
    icon: CircleGauge,
    title: "Brake Rotors",
    text: "Warped or below-spec rotors cause pulsing pedals and shaking steering wheels. We measure on site and replace with quality rotors when needed.",
  },
  {
    icon: Wrench,
    title: "Brake Calipers",
    text: "Sticking or seized calipers cause pulling, uneven wear and burning smells. We diagnose, replace and lubricate slides so new parts last.",
  },
  {
    icon: Wrench,
    title: "Brake Hardware",
    text: "Clips, shims, slide pins and abutment hardware — the small parts that prevent noise, dragging and uneven wear. Serviced or replaced on every job, never skipped.",
  },
  {
    icon: Search,
    title: "Brake Inspections",
    text: "Not sure what you need? We measure pad thickness and rotor spec, check calipers and hardware, and give you an honest answer — even if it's 'not yet.'",
  },
];

const SYMPTOMS = [
  "Squealing or screeching when braking",
  "Grinding — metal-on-metal sound",
  "Steering wheel shakes when stopping",
  "Brake pedal feels soft or sinks",
  "Car pulls to one side under braking",
  "Brake warning light on the dash",
];

export default function BrakeReplacement() {
  return (
    <>
      <SEO
        title="Mobile Brake Replacement | Middlesex County MA & Southern NH"
        description="Mobile brake replacement at your home or workplace — brake pads, rotors, calipers and inspections across Middlesex County, MA and Southern NH. Get an exact quote online."
        path="/brake-replacement"
      />

      <section className="grain relative overflow-hidden border-b border-edge">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <Reveal>
            <Eyebrow>Mobile Brake Replacement · We Come to You</Eyebrow>
            <h1 className="mt-4 max-w-4xl font-display text-4xl font-black uppercase leading-[0.95] tracking-tight text-slate-50 sm:text-5xl lg:text-6xl">
              Brake Pads & Rotors Replaced in <span className="text-crimson">Your Driveway</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Looking for brake replacement near you — without the shop? My Mobile Mechanic brings
              professional brake repair to your home or workplace across Middlesex County,
              Massachusetts and Southern New Hampshire. Front brakes, rear brakes, pads, rotors and
              calipers — done on site, backed by warranty, priced upfront.
            </p>
            <CTAPair className="mt-8" />
          </Reveal>
        </div>
      </section>

      <section data-testid="brake-services-section" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading
          eyebrow="What We Service"
          title="Every Part of Your Brake System"
          sub="From a quick inspection to a full four-corner brake job, everything happens where your car is parked."
        />
        <div className="mt-12 grid gap-px border border-edge bg-edge sm:grid-cols-2 lg:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 3) * 0.08} className="h-full">
              <div className="flex h-full flex-col bg-obsidian p-7 transition-colors duration-300 hover:bg-slate2">
                <s.icon className="h-7 w-7 text-amber2" />
                <h2 className="mt-5 font-display text-2xl font-bold uppercase tracking-tight text-slate-50">{s.title}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-400">{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section data-testid="brake-warranty-section" className="border-y border-edge bg-emerald-50">
        <div className="mx-auto flex max-w-7xl flex-col items-start gap-4 px-4 py-10 sm:flex-row sm:items-center sm:px-6">
          <CheckCircle2 className="h-10 w-10 shrink-0 text-emerald-600" />
          <div>
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight text-slate-50">
              3-Year / 36,000-Mile Warranty
            </h2>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-slate-300">
              Every brake job is backed by a 3-year / 36,000-mile parts-and-labor warranty when My
              Mobile Mechanic supplies the parts — in writing, with your invoice. Old parts are
              always shown to you before we leave.
            </p>
          </div>
        </div>
      </section>

      <section className="border-b border-edge bg-slate2">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 py-16 sm:px-6 sm:py-24 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Warning Signs</Eyebrow>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-slate-50 sm:text-4xl">
              Is Your Car Telling You It's Time?
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              Brakes rarely fail without warning. If you notice any of these, request a quote —
              catching worn pads early is the difference between a simple pad swap and a full
              pad-and-rotor job.
            </p>
            <QuoteButton className="mt-8" testid="brake-page-quote-button" />
          </Reveal>
          <Reveal delay={0.15}>
            <ul className="space-y-3">
              {SYMPTOMS.map((s) => (
                <li key={s} className="flex items-center gap-3 border border-edge bg-obsidian px-5 py-4">
                  <AlertTriangle className="h-5 w-5 shrink-0 text-amber2" />
                  <span className="text-sm font-semibold text-slate-200">{s}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <SectionHeading eyebrow="Where We Work" title="Mobile Brake Replacement Near You" />
        <Reveal className="mt-8 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {LOCATIONS.map((l) => (
            <Link
              key={l.slug}
              to={`/${l.slug}`}
              data-testid={`brake-page-location-${l.city.toLowerCase()}-link`}
              className="border border-edge bg-slate2 px-5 py-4 font-display text-lg font-bold uppercase text-slate-200 transition-colors hover:border-crimson hover:text-crimson"
            >
              {l.city}, {l.st}
            </Link>
          ))}
        </Reveal>
        <Reveal className="mt-8 flex items-start gap-3 border border-edge bg-slate2 p-5">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-amber2" />
          <p className="text-sm leading-relaxed text-slate-300">
            Every brake job includes quality parts, proper bedding-in, a post-repair check and a
            written warranty — and you never leave your driveway.
          </p>
        </Reveal>
      </section>

      <div className="border-t border-edge bg-slate2/40">
        <FAQSection />
      </div>
      <CTABand title="Stop the Squeal. Book From Your Phone." />
    </>
  );
}
